import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import StoreForm from "../../components/StoreForm/StoreForm";
import useAdminStores from "../../hooks/useAdminStores";
import useAdminRegions from "../../hooks/useAdminRegions";
import useAdminAreas from "../../hooks/useAdminAreas";
import useEditStore from "../../hooks/useEditStore";
import useDeleteStore from "../../hooks/useDeleteStore";
import { adaptStorePayload } from "../../adapters/adaptStorePayload";

const BACK_PATH = "/users/locations";

const EMPTY_DEFAULTS: StoreFormValues = {
	name: "",
	region: "",
	area: ""
};

function storeToFormValues(store: Store): StoreFormValues {
	return {
		name: store.name,
		region: store.region ? String(store.region.id) : "",
		area: store.area ? String(store.area.id) : ""
	};
}

const EditStore = () => {
	const navigate = useNavigate();
	const { storeId } = useParams<{ storeId: string }>();
	const id = Number(storeId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] =
		useState<StoreFormValues>(EMPTY_DEFAULTS);
	const [selectedRegion, setSelectedRegion] = useState("");

	const { data: stores = [], isLoading: isLoadingStores } = useAdminStores();
	const { data: regions = [] } = useAdminRegions();
	const { data: areas = [] } = useAdminAreas(
		selectedRegion ? Number(selectedRegion) : undefined
	);
	const { mutate: editStore, isLoading: isEditing } = useEditStore();
	const { mutate: deleteStore, isLoading: isDeleting } = useDeleteStore();

	const store = stores.find((s) => s.id === id);

	useEffect(() => {
		if (store) {
			const values = storeToFormValues(store);
			setDefaultValues(values);
			setSelectedRegion(values.region);
		}
	}, [store]);

	if (!isValidId) {
		return <Navigate to={BACK_PATH} replace />;
	}

	if (isLoadingStores) {
		return (
			<Page>
				<div className="flex items-center gap-2 py-12 justify-center">
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
					<span className="text-sm text-zinc-500">Loading...</span>
				</div>
			</Page>
		);
	}

	if (!store) {
		return (
			<Page>
				<p className="text-sm text-destructive">Store not found.</p>
			</Page>
		);
	}

	const handleSubmit = (values: StoreFormValues) => {
		const payload = adaptStorePayload(values);

		editStore(
			{ id, payload },
			{
				onSuccess: () => navigate(BACK_PATH)
			}
		);
	};

	const handleDelete = () => {
		deleteStore(id, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate(BACK_PATH);
			}
		});
	};

	return (
		<Page>
			<StoreForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				regions={regions}
				areas={areas}
				backPath={BACK_PATH}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				onRegionChange={setSelectedRegion}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete store?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditStore;
